import apiClient from './client';

/**
 * Enhanced Ticket API Client - v2.0
 * All requests automatically include auth cookies
 * No dummy data - everything from backend
 */

/**
 * Create a new ticket with file uploads
 */
export const createTicket = async (formData) => {
  const response = await apiClient.post('/tickets', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Get my tickets with server-side pagination & search
 */
export const getMyTickets = async (params = {}) => {
  const response = await apiClient.get('/tickets', { params });
  return response.data;
};

/**
 * Get all tickets (SUPERADMIN only) with server-side pagination & search
 */
export const getAllTickets = async (params = {}) => {
  const response = await apiClient.get('/tickets/admin/all', { params });
  return response.data;
};

/**
 * Get single ticket by ID
 */
export const getTicketById = async (id) => {
  const response = await apiClient.get(`/tickets/${id}`);
  return response.data;
};

/**
 * Update own ticket
 */
export const updateTicket = async (id, data) => {
  const response = await apiClient.patch(`/tickets/${id}`, data);
  return response.data;
};

/**
 * Delete own ticket
 */
export const deleteTicket = async (id) => {
  const response = await apiClient.delete(`/tickets/${id}`);
  return response.data;
};

/**
 * Assign ticket to user (SUPERADMIN only)
 */
export const assignTicket = async (id, data) => {
  const response = await apiClient.patch(`/tickets/${id}/assign`, data);
  return response.data;
};

/**
 * Change ticket status (SUPERADMIN only)
 */
export const changeTicketStatus = async (id, status) => {
  const response = await apiClient.patch(`/tickets/${id}/status`, { status });
  return response.data;
};

/**
 * Force delete ticket (SUPERADMIN only)
 */
export const forceDeleteTicket = async (id) => {
  const response = await apiClient.delete(`/tickets/${id}/force`);
  return response.data;
};

/**
 * Get ticket comments (with nested replies)
 */
export const getTicketComments = async (ticketId) => {
  const response = await apiClient.get(`/comments/${ticketId}`);
  return response.data;
};

/**
 * Add comment to ticket (supports nested replies and file uploads)
 */
export const addComment = async (formData) => {
  const response = await apiClient.post('/comments', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Update own comment
 */
export const updateComment = async (id, data) => {
  const response = await apiClient.patch(`/comments/${id}`, data);
  return response.data;
};

/**
 * Delete comment
 */
export const deleteComment = async (id) => {
  const response = await apiClient.delete(`/comments/${id}`);
  return response.data;
};

/**
 * Get comprehensive dashboard statistics
 * Returns: overview, byStatus, byPriority, byCategory, timeStats, recentActivity, myStats/teamStats
 */
export const getDashboardStats = async () => {
  const response = await apiClient.get('/analytics/dashboard');
  return response.data;
};

/**
 * Get tickets trend (12 months data)
 */
export const getTicketsTrend = async () => {
  const response = await apiClient.get('/analytics/tickets-trend');
  return response.data;
};

/**
 * Get tickets by status (with pagination)
 */
export const getTicketsByStatus = async (params = {}) => {
  const response = await apiClient.get('/analytics/tickets-by-status', { params });
  return response.data;
};

/**
 * Get tickets by priority (with pagination)
 */
export const getTicketsByPriority = async (params = {}) => {
  const response = await apiClient.get('/analytics/tickets-by-priority', { params });
  return response.data;
};

/**
 * Get category breakdown
 */
export const getByCategory = async () => {
  const response = await apiClient.get('/analytics/by-category');
  return response.data;
};

/**
 * Get resolution time statistics
 */
export const getResolutionTime = async () => {
  const response = await apiClient.get('/analytics/resolution-time');
  return response.data;
};

/**
 * Get user tickets statistics (SUPERADMIN only, with pagination & search)
 */
export const getUserTickets = async (params = {}) => {
  const response = await apiClient.get('/analytics/user-tickets', { params });
  return response.data;
};

/**
 * Download an attachment
 */
export const downloadAttachment = async (attachmentId) => {
  const response = await apiClient.get(`/tickets/attachment/${attachmentId}`, {
    responseType: 'blob',
  });
  return response;
};

/**
 * Add attachments to existing ticket
 */
export const addAttachmentsToTicket = async (ticketId, formData) => {
  const response = await apiClient.post(`/tickets/${ticketId}/attachments`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Get activity logs for a ticket
 */
export const getActivityLogs = async (ticketId) => {
  const response = await apiClient.get(`/activity/${ticketId}`);
  return response.data;
};

const ticketApi = {
  createTicket,
  getMyTickets,
  getAllTickets,
  getTicketById,
  updateTicket,
  deleteTicket,
  assignTicket,
  changeTicketStatus,
  forceDeleteTicket,
  getTicketComments,
  addComment,
  updateComment,
  deleteComment,
  getDashboardStats,
  getTicketsTrend,
  getTicketsByStatus,
  getTicketsByPriority,
  getByCategory,
  getResolutionTime,
  getUserTickets,
  downloadAttachment,
};

export default ticketApi;
